import React, { Fragment, Component } from 'react';
import { connect } from 'react-redux';
import { compose } from 'redux';
import Media from 'react-media';
import { Field } from 'formik';
import Yup from 'yup';
import get from 'lodash/get';
import { Alert } from '@lightspeed/flame/Alert';
import { Button } from '@lightspeed/flame/Button';
import Group, { GroupAddon } from '@lightspeed/cirrus-group';
import Input from '@lightspeed/cirrus-input';
import Select from '@lightspeed/cirrus-select';
import Text from '@lightspeed/cirrus-text';
import { entities } from 'pages/store/schema';
import { getValues, getErrors } from 'pages/store/selectors';
import { actionTypes } from 'pages/store/actions/defaults';
import FormField from 'cirrus/FormField';
import { formikWithInitialValidation } from 'pages/components/Formik/enhancers';
import FormikInput from 'pages/components/Formik/Input';
import { putData } from 'pages/store/actions/data';
import { breakpoints } from 'pages/utils/media';
import Receipt from '../components/Receipt';
import ReceiptAddressPlaceholder from '../components/Receipt/AddressPlaceholder';
import ReceiptSaleLines from '../components/Receipt/SaleLines';
import { validateTaxRate } from '../utils';
import FormHeader from '../components/FormHeader';

const handleTaxKeyDown = e => {
  const { valueAsNumber } = e.target;
  const charCode = String.fromCharCode(e.keyCode);

  if (charCode === 'E' || (/\d/.test(charCode) && !validateTaxRate(valueAsNumber))) {
    e.preventDefault();
  }
};

class TaxSettingsForm extends Component {
  componentWillUnmount() {
    const { values, isValid, isSubmitting } = this.props;
    if (isValid && !isSubmitting) {
      try {
        this.props.putData('/admin/first_start/tax_setup', { taxSetup: values });
      } catch (error) {
        // Background save, no error handling
      }
    }
  }

  render() {
    const {
      data,
      errors,
      values,
      setFieldValue,
      handleSubmit,
      isValid,
      isSubmitting,
      countryCode,
      loadingErrors,
      savingErrors,
    } = this.props;

    return (
      <Fragment>
        <FormHeader
          title="Now it’s time for taxes!"
          description="Configure the tax model and rate that will be applied to sales. (You can change this later.)"
        />
        {loadingErrors || savingErrors ? (
          <Alert type="danger" noCloseBtn className="cr-m-2">
            <Text>An error has occurred. Please reload the page and try again</Text>
          </Alert>
        ) : null}
        <div className="cr-flex cr-p-2">
          <form
            className="cr-flex-grow cr-p-2"
            data-test="tax-settings-form"
            onSubmit={handleSubmit}
          >
            <div className="cr-mb-3">
              <FormField
                label="Tax Model"
                status={
                  errors.taxModel
                    ? {
                        type: 'error',
                        message: errors.taxModel,
                      }
                    : null
                }
              >
                <Select
                  value={values.taxModel}
                  data-test="tax-model-select"
                  onChange={event => {
                    const { value } = event.target;
                    setFieldValue('taxModel', value);
                  }}
                >
                  <option value="tax_inclusive">
                    Inclusive (Sales tax is included in the item prices)
                  </option>
                  <option value="tax_exclusive">
                    Exclusive (Sales tax is excluded from the item prices)
                  </option>
                </Select>
              </FormField>
            </div>
            <div className="cr-mb-3 cr-flex">
              <div className="cr-width-6-12 cr-mr-2">
                <Field
                  name="tax1Rate"
                  render={fieldProps => (
                    <FormikInput
                      {...fieldProps}
                      label={get(data, 'tax1.name')}
                      type="number"
                      step="0.0000001"
                      onKeyDown={handleTaxKeyDown}
                      style={{ textAlign: 'right' }}
                      data-test="tax1-rate-input"
                    >
                      {inputProps => (
                        <Group noSpacing inputBlock>
                          <Input {...inputProps} />
                          <GroupAddon>%</GroupAddon>
                        </Group>
                      )}
                    </FormikInput>
                  )}
                />
              </div>

              {get(data, 'tax2.visible') && (
                <div className="cr-width-6-12">
                  <Field
                    name="tax2Rate"
                    render={fieldProps => (
                      <FormikInput
                        {...fieldProps}
                        label={get(data, 'tax2.name')}
                        type="number"
                        step="0.0000001"
                        onKeyDown={handleTaxKeyDown}
                        style={{ textAlign: 'right' }}
                        data-test="tax2-rate-input"
                      >
                        {inputProps => (
                          <Group noSpacing inputBlock>
                            <Input {...inputProps} />
                            <GroupAddon>%</GroupAddon>
                          </Group>
                        )}
                      </FormikInput>
                    )}
                  />
                </div>
              )}
            </div>
            <div className="cr-mt-4">
              <Media query={{ minWidth: breakpoints.md }}>
                {matches => (
                  <Button
                    variant="primary"
                    block={!matches}
                    fill
                    size={!matches ? 'large' : null}
                    onClick={handleSubmit}
                    loading={isSubmitting}
                    disabled={!isValid}
                    data-test="tax-settings-submit-button"
                  >
                    Go to My Shop
                  </Button>
                )}
              </Media>
            </div>
          </form>

          <Media
            query={{ minWidth: breakpoints.lg }}
            render={() => (
              <div className="cr-flex cr-justify-end cr-width-5-12 cr-p-2">
                <Receipt>
                  <ReceiptAddressPlaceholder />
                  <ReceiptSaleLines
                    {...values}
                    tax1Name={get(data, 'tax1.name')}
                    tax2Name={get(data, 'tax2.name')}
                    countryCode={countryCode}
                  />
                </Receipt>
              </div>
            )}
          />
        </div>
      </Fragment>
    );
  }
}

const enhancedForm = compose(
  formikWithInitialValidation({
    mapPropsToValues: props => ({
      shopId: '1',
      taxModel: get(props, 'data.taxModel', ''),
      tax1Rate: get(props, 'data.tax1.currentRate', ''),
      tax2Rate: get(props, 'data.tax2.currentRate', ''),
    }),
    validationSchema: ({ data } = {}) =>
      Yup.object().shape({
        taxModel: Yup.string()
          .required()
          .oneOf(['tax_inclusive', 'tax_exclusive']),
        tax1Rate: Yup.number()
          .test('tax1Rate', `${get(data, 'tax1.name')} is not valid`, validateTaxRate)
          .required(`${get(data, 'tax1.name')} is required`),
        ...(get(data, 'tax2.visible') && {
          tax2Rate: Yup.number()
            .test('tax2Rate', `${get(data, 'tax2.name')} is not valid`, validateTaxRate)
            .required(`${get(data, 'tax2.name')} is required`),
        }),
      }),
    handleSubmit: async (values, formikBag) => {
      formikBag.setSubmitting(true);

      if (window.isGriffin()) {
        window.StencilWebAPI.navigation.setBusyState(true);
      }

      try {
        await formikBag.props.putData('/admin/first_start/tax_setup', { taxSetup: values });
      } catch (error) {
        if (error.errors) formikBag.setErrors(error.errors);
        formikBag.setSubmitting(false);
        return;
      }

      const systemCustomerID = window.merchantos.session.getSystemCustomerID();

      await window.lsCloud.startServerJob({
        url: `/API/Account/${systemCustomerID}/Option`,
        obj: [
          {
            Option: {
              name: 'first_start_status',
              value: 'tutorial',
            },
          },
        ],
      });

      if (window.isGriffin()) {
        window.StencilWebAPI.Utils.postMessage(
          window.StencilWebAPI.Utils.MessageHandler.FIRST_START_COMPLETE_HANDLER,
          {}
        );
      } else {
        window.location.href = '/?form_name=ui_tab&tab=register';
      }

      if (window.isGriffin()) {
        window.StencilWebAPI.navigation.setBusyState(false);
      }
    },
  })
)(TaxSettingsForm);

const mapStateToProps = state => ({
  countryCode: getValues(entities.shopDetails)(state).shopSetup.countryCode,
  loadingErrors: getErrors({
    entity: entities.taxSettings,
    list: false,
    actionType: actionTypes.LOADING,
  })(state),
  savingErrors: getErrors({
    entity: entities.taxSettings,
    list: false,
    actionType: actionTypes.SAVING,
  })(state),
});

const mapDispatchToProps = dispatch => ({
  putData: (url, data, onSuccess) =>
    dispatch(putData({ url, payload: data, entity: entities.taxSettings, list: false, onSuccess })),
});

export default connect(mapStateToProps, mapDispatchToProps)(enhancedForm);
